import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Chip, 
  Grid,
  Tabs,
  Tab,
  Button,
  Avatar,
  LinearProgress
} from '@mui/material';
import { 
  ArrowBack, 
  CalendarToday, 
  LocationOn, 
  People,
  CheckCircle,
  Schedule,
  Cancel,
  Assignment,
  Add
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';

interface Campaign {
  id: string;
  title: string;
  status: '대기중' | '모집중' | '선정발표' | '등록기간' | '종료';
  createdDate: string;
  deadline: string;
  location: string;
  applicants: number;
  maxApplicants: number;
  image: string;
  progress: number;
}

const mockCampaigns: Campaign[] = [
  {
    id: '1',
    title: '서울 강남구 맛집 탐방',
    status: '모집중',
    createdDate: '2024-01-15',
    deadline: '2024-02-28',
    location: '서울 강남구',
    applicants: 23,
    maxApplicants: 30,
    image: '/api/placeholder/300/200',
    progress: 77
  },
  {
    id: '2',
    title: '부산 해운대 카페 투어',
    status: '선정발표',
    createdDate: '2024-01-10',
    deadline: '2024-03-15',
    location: '부산 해운대구',
    applicants: 18,
    maxApplicants: 25,
    image: '/api/placeholder/300/200',
    progress: 72
  },
  {
    id: '3',
    title: '제주도 숙박 체험',
    status: '대기중',
    createdDate: '2024-01-05',
    deadline: '2024-04-30',
    location: '제주도',
    applicants: 0,
    maxApplicants: 15,
    image: '/api/placeholder/300/200',
    progress: 0
  }
];

const getStatusColor = (status: string) => {
  switch (status) {
    case '대기중': return 'default';
    case '모집중': return 'primary';
    case '선정발표': return 'warning';
    case '등록기간': return 'info';
    case '종료': return 'success';
    default: return 'default';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case '대기중': return <Schedule />;
    case '모집중': return <Assignment />;
    case '선정발표': return <CheckCircle />;
    case '등록기간': return <Assignment />;
    case '종료': return <CheckCircle />;
    default: return <Schedule />;
  }
};

const AdvertiserCampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    navigate('/mypage/advertiser');
  };

  const handleCampaignClick = (campaignId: string) => {
    navigate(`/campaign/${campaignId}`);
  };

  const handleCreateCampaign = () => {
    navigate('/campaign/create');
  };

  const filteredCampaigns = mockCampaigns.filter(campaign => {
    if (tabValue === 0) return campaign.status === '대기중';
    if (tabValue === 1) return campaign.status === '모집중';
    if (tabValue === 2) return campaign.status === '선정발표';
    if (tabValue === 3) return campaign.status === '등록기간';
    if (tabValue === 4) return campaign.status === '종료';
    return true;
  });

  return (
    <>
      <Header showIcons={true} />
      <Container maxWidth="md" sx={{ pb: 8, pt: 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              sx={{ mr: 2 }}
            >
              뒤로
            </Button>
            <Typography variant="h5" component="h1">
              나의 캠페인
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateCampaign}
          >
            캠페인 등록
          </Button>
        </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
              px: 2,
              py: 1.5
            },
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': {
                opacity: 0.3
              }
            }
          }}
        >
          <Tab label="대기중" />
          <Tab label="모집중" />
          <Tab label="선정발표" />
          <Tab label="등록기간" />
          <Tab label="종료" />
        </Tabs>
      </Box>

      {/* Campaign List */}
      <Box>
        {filteredCampaigns.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {tabValue === 0 && '대기중인 캠페인이 없습니다'}
              {tabValue === 1 && '모집중인 캠페인이 없습니다'}
              {tabValue === 2 && '선정발표 단계인 캠페인이 없습니다'}
              {tabValue === 3 && '등록기간인 캠페인이 없습니다'}
              {tabValue === 4 && '종료된 캠페인이 없습니다'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              새로운 캠페인을 등록해보세요!
            </Typography>
            <Button 
              variant="contained" 
              sx={{ mt: 2 }}
              onClick={handleCreateCampaign}
            >
              캠페인 등록하기
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredCampaigns.map((campaign) => (
              <Grid item xs={12} key={campaign.id}>
                <Card 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleCampaignClick(campaign.id)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Avatar
                        src={campaign.image}
                        variant="rounded"
                        sx={{ width: 100, height: 100, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography 
                            variant="h6" 
                            component="h2" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '1.1rem',
                              lineHeight: 1.3,
                              mb: 1
                            }}
                          >
                            {campaign.title}
                          </Typography>
                          <Chip
                            icon={getStatusIcon(campaign.status)}
                            label={campaign.status}
                            color={getStatusColor(campaign.status) as any}
                            size="small"
                            sx={{ 
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              height: '24px'
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn fontSize="small" color="action" />
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: '0.9rem' }}
                            >
                              {campaign.location}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday fontSize="small" color="action" />
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: '0.9rem' }}
                            >
                              등록일: {campaign.createdDate} | 마감일: {campaign.deadline}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <People fontSize="small" color="action" />
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: '0.9rem' }}
                              >
                                {campaign.applicants}/{campaign.maxApplicants}명
                              </Typography>
                            </Box>
                            <Typography 
                              variant="body2" 
                              color="primary" 
                              sx={{ 
                                fontWeight: 600,
                                fontSize: '0.95rem'
                              }}
                            >
                              {campaign.progress}%
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={campaign.progress} 
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            variant="outlined" 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCampaignClick(campaign.id);
                            }}
                            sx={{ 
                              flex: 1,
                              fontSize: '0.9rem',
                              fontWeight: 500
                            }}
                          >
                            상세보기
                          </Button>
                          <Button 
                            variant="contained" 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              // TODO: Implement edit functionality
                              console.log('Edit campaign:', campaign.id);
                            }}
                            sx={{ 
                              flex: 1,
                              fontSize: '0.9rem',
                              fontWeight: 500
                            }}
                          >
                            수정
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      </Container>
    </>
  );
};

export default AdvertiserCampaignsPage; 